import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Container, Typography, Grid, Box, Paper, Chip, Alert, CircularProgress,
  FormControl, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton, SxProps, Theme,
  InputAdornment, Drawer, IconButton as MuiIconButton, TableContainer, Popover
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CloseIcon from '@mui/icons-material/Close';
import { getUserFriendlyError } from '../utils/errorHandler';
import { Favorite } from '@mui/icons-material';
import AccountPickerMulti from '../components/AccountPickerMulti';
import type { AccountGroup } from '../components/AccountPickerMulti';
import { useFieldValidation } from '../hooks/useFieldValidation';
import type { FieldLimit, PairRule, CrossRule } from '../hooks/useFieldValidation';

// ==== Границы полей: 1-в-1 из StartReactorsCommand (pydantic ge/le) ====
const NR_LIMITS: Record<string, FieldLimit> = {
  minDelay: { min: 3, max: 60, integer: true },       // min_delay ge=3 le=60
  maxDelay: { min: 5, max: 300, integer: true },      // max_delay ge=5 le=300
  maxSpreadHours: { min: 1, max: 72, integer: true }, // max_spread_hours ge=1 le=72
  // reactions_count: Optional[int] ge=0 (пусто = реагируют все)
  reactionsCount: { min: 0, integer: true, optional: true },
  // min_views_count: Optional[int] без ge/le; по UI минимум 1 (пусто = смотрят все)
  minViewsCount: { min: 1, integer: true, optional: true },
};

const NR_PAIRS: PairRule[] = [
  { minKey: 'minDelay', maxKey: 'maxDelay' },
];

// Кросс-правило: число просматривающих не меньше числа реагирующих (по подсказке в UI).
// Проверяем только когда оба поля заданы (оба Optional).
const NR_CROSS: CrossRule[] = [
  {
    targetKey: 'minViewsCount',
    validate: (v) => {
      const views = v.minViewsCount?.trim();
      const reacts = v.reactionsCount?.trim();
      if (!views || !reacts) return null;
      const vi = Number(views);
      const re = Number(reacts);
      if (!Number.isNaN(vi) && !Number.isNaN(re) && vi < re) {
        return 'Не меньше числа реагирующих';
      }
      return null;
    },
  },
];

interface Account {
  id: string;
  name: string;
  phone_number: string;
}

interface ActiveReactor {
  job_id: string;
  channels: string[];
  accounts_count: number;
  status: string;
  started_at: string;
  stopped_at?: string;
  total_views_added: number;
  total_reactions_added: number;
  total_errors: number;
  last_activity_at?: string;
}

interface JobMeta {
  job_id: string;
  user_id: string;
  account_names: string[];
  channels: string[];
  reactions: string[];
  min_delay: number;
  max_delay: number;
  started_at: string;
  spread_reactions: boolean;
  max_spread_hours: number;
}

interface AccountStats {
  reactions_added: number;
  errors: number;
  last_reaction_at?: string;
}
interface ReactionStats {
  total_messages_seen: number;
  total_reactions_added: number;
  total_errors: number;
  accounts_stats: Record<string, AccountStats>;
  last_reaction_at?: string;
  last_activity_at?: string;
  last_error?: { type: string; message: string; timestamp: string };
  started_at: string;
  accounts_count: number;
  channels: string[];
}

interface ProcessInfo {
  job_id: string;
  pid: number;
  status: string;
  accounts_count: number;
  channels: string[];
  started_at: string;
  stopped_at?: string;
  died_at?: string;
}
interface ReactionStatusResponse {
  job_id: string;
  meta: JobMeta | null;
  stats: ReactionStats | null;
  process: ProcessInfo | null;
  error?: string;
}

interface ReactorForm {
  accountNames: string[];
  channels: string;           // строка для ввода, каналы через запятую
  reactions: string[];
  minDelay: string;
  maxDelay: string;
  spreadReactions: boolean;
  maxSpreadHours: string;
  reactionsCount: string;
  randomizeReactionsCount: boolean;
  minViewsCount: string;
}



const POPULAR_EMOJI = ['👍', '❤️', '🔥', '👏', '😂', '😢', '💪', '🎉', '🙏', '🤔', '😎', '💯' ,'👎','💋','🧐','🕊️','😴','🤓','💩','🤡','😈',];

const NeuroReactor: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [activeJobs, setActiveJobs] = useState<ActiveReactor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [tab, setTab] = useState<'setup' | 'active'>('setup');

  const [form, setForm] = useState<ReactorForm>({
    accountNames: [],
    channels: '',
    reactions: ['❤️'],
    minDelay: '5',
    maxDelay: '30',
    spreadReactions: true,
    maxSpreadHours: '3',
    reactionsCount: '',
    randomizeReactionsCount: false,
    minViewsCount: '',
  });

  const update = (key: keyof ReactorForm, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const validation = useFieldValidation({ limits: NR_LIMITS, pairs: NR_PAIRS, cross: NR_CROSS });

  const addReaction = (emoji: string) => {
    if (!form.reactions.includes(emoji) && form.reactions.length < 10) {
      update('reactions', [...form.reactions, emoji]);
    }
  };

  const removeReaction = (emoji: string) => {
    update('reactions', form.reactions.filter(r => r !== emoji));
  };

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    const interval = setInterval(fetchActive, 5000);
    return () => clearInterval(interval);
  }, [auth?.isAuthenticated, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accRes, jobsRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/reactors/jobs')
      ]);
      api.get('/account_groups').then(r => setGroups(r.data || [])).catch(() => {});
      setAccounts(accRes.data.accounts || []);
      setActiveJobs(jobsRes.data.jobs || []);
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка загрузки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActive = async () => {
    try {
      const res = await api.get('/reactors/jobs');
      setActiveJobs(res.data.jobs || []);
    } catch (err) {
      console.error('Ошибка обновления:', err);
    }
  };

  const start = async () => {
    const parsedChannels = form.channels
      .split(',')
      .map(c => c.trim())
      .filter(Boolean);

    if (!form.accountNames.length || parsedChannels.length === 0) {
      setMessage({ text: 'Выберите аккаунты и укажите хотя бы один канал', type: 'error' });
      return;
    }
    if (form.reactions.length === 0) {
      setMessage({ text: 'Выберите хотя бы одну реакцию', type: 'error' });
      return;
    }

    if (!validation.validateAll(form)) {
      setMessage({ text: 'Проверьте выделенные поля — есть ошибки валидации', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/reactors/start', {
        account_names: form.accountNames,
        channels: parsedChannels,
        reactions: form.reactions,
        min_delay: Number(form.minDelay) || 5,
        max_delay: Number(form.maxDelay) || 30,
        spread_reactions: form.spreadReactions,
        max_spread_hours: Number(form.maxSpreadHours) || 3,
        reactions_count: form.reactionsCount ? Number(form.reactionsCount) : null,
        randomize_reactions_count: form.randomizeReactionsCount,
        min_views_count: form.minViewsCount ? Number(form.minViewsCount) : null,
      });

      setMessage({ text: 'Накрутка реакций запущена!', type: 'success' });
      setTab('active');
      update('channels', '');
      fetchActive();
    } catch (err: any) {
      const text = getUserFriendlyError(err, accounts, 'накрутку реакций');
      setMessage({ text, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const stop = async (jobId: string) => {
    setIsLoading(true);
    try {
      await api.get(`/reactors/stop/${jobId}`);
      setMessage({ text: 'Остановлено', type: 'info' });
      fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка остановки', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const gridItem: SxProps<Theme> = { display: 'flex', alignItems: 'center', width: '100%' };

  const getTabStyle = (current: 'setup' | 'active') => ({
    flex: 1,
    py: 2,
    borderRadius: 0,
    bgcolor: tab === current ? '#E0F2FE' : 'transparent',
    color: tab === current ? '#1E40AF' : '#6B7280',
    fontWeight: tab === current ? 600 : 400,
    '&:hover': { bgcolor: tab === current ? '#DBEAFE' : '#F3F4F6' }
  });

  if (isLoading && accounts.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: '#F8FAFC', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Favorite sx={{ fontSize: 40, color: '#1E40AF' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1E3A8A', mb: 1 }}>
              Накрутка реакций
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Реакции" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              <Chip label="Просмотры" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
              <Chip label="Имитация активности подписчиков" size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }} />
            </Box>
            <Typography sx={{ color: '#4B5563', fontSize: '0.95rem', lineHeight: 1.6 }}>
              Аккаунты ставят реакции и просматривают новые посты — с задержками и рандомизацией.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Как работает накрутка реакций?
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Ваши аккаунты мониторят новые посты, просматривают и ставят реакции, имитируя реальную активность подписчиков
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Мультиаккаунт</Typography>
            <Typography sx={{color: '#4B5563', fontSize: '0.9rem'}}>
              Можно запустить все аккаунты сразу или указать, сколько из них будет
              ставить реакции на каждый пост. Остальные будут только создавать
              просмотры — это выглядит максимально естественно.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Антибан-система</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Случайные задержки о между реакциями и рандомный выбор эмодзи из списка позволяют создать картину вовлеченности аудитории.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Растягивание по времени</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Можно растянуть реакции на 1–72 часа после поста — выглядит максимально естественно.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Полная статистика</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Сколько реакций поставлено, сколько ошибок, время последней активности — всё видно в реальном времени.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Управление задачами</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Остановить, возобновить или удалить задачу можно в любой момент. Остановленные задачи можно запустить снова
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#1E40AF', fontWeight: 500, mb: 1 }}>Ограничения</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '0.9rem' }}>
              Аккаунты должны быть подписаны на целевой канал для проставления реакций. Также необходимо учитывать, что администратор канала может заблокировать ряд эмодзи для реагирования - в таком случае в статистике реактора отразится ошибка
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic' }}>
          Подходит для: продвижения каналов, конкурсов, SMM-задач, личных проектов
        </Typography>
         <Typography sx={{color: '#1E40AF', mt: 4, fontWeight: 600, fontStyle: 'italic'}}>
          Ознакомьтесь с документацией по автоматизации{' '}
          <Typography
              component="a"
              href="https://typhon.gitbook.io/docs/automatizations_telegram/reactions_and_views"
              sx={{textDecoration: 'underline', cursor: 'pointer'}}
          >
            здесь
          </Typography>
          </Typography>
      </Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex' }}>
          <Button onClick={() => setTab('setup')} sx={getTabStyle('setup')} startIcon={<PlayArrowIcon />}>
            Настройка
          </Button>
          <Button onClick={() => setTab('active')} sx={getTabStyle('active')} startIcon={<StopIcon />}>
            Мои реакторы
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {tab === 'setup' && (
            <SetupTab
              form={form}
              update={update}
              accounts={accounts}
              groups={groups}
              start={start}
              isLoading={isLoading}
              gridItem={gridItem}
              addReaction={addReaction}
              removeReaction={removeReaction}
              validation={validation}
            />
          )}
          {tab === 'active' && (
              <ActiveTab
                  jobs={activeJobs}
                  stop={stop}
                  isLoading={isLoading}
                  setMessage={setMessage}     // ← НОВАЯ СТРОКА
                  fetchActive={fetchActive}   // ← НОВАЯ СТРОКА
                  accounts={accounts}
              />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

const AccountPicker: React.FC<{
  accounts: Account[];
  selected: string[];
  onChange: (names: string[]) => void;
}> = ({ accounts, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  const filtered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).slice(0, 50);

  const filteredNames = filtered.map(a => a.name);
  const allFilteredSelected = filteredNames.length > 0 && filteredNames.every(n => selected.includes(n));
  const totalFiltered = accounts.filter(acc =>
    acc.name.toLowerCase().includes(search.toLowerCase()) ||
    acc.phone_number?.includes(search)
  ).length;

  const toggle = (name: string) =>
    onChange(selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name]);

  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      onChange(selected.filter(n => !filteredNames.includes(n)));
    } else {
      onChange([...selected, ...filteredNames.filter(n => !selected.includes(n))]);
    }
  };

  return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
      {/* Триггер */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          width: '100%',
          border: open ? '2px solid #1E40AF' : '1px solid #C4C4C4',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          minHeight: 56,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          boxSizing: 'border-box',
          bgcolor: 'white',
          '&:hover': { borderColor: '#1E40AF' },
        }}
      >
        {selected.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>Выберите аккаунты</Typography>
        ) : (
          <Typography sx={{ fontSize: '1rem', color: '#1E40AF', fontWeight: 500 }}>
            Выбрано {selected.length} из {accounts.length} аккаунтов
          </Typography>
        )}
      </Box>

      {/* Дропдаун — абсолютно под триггером, та же ширина */}
      {open && (
        <>
          {/* Оверлей для закрытия по клику вне */}
          <Box
            onClick={() => { setOpen(false); setSearch(''); }}
            sx={{ position: 'fixed', inset: 0, zIndex: 1200 }}
          />
          <Box sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            zIndex: 1300,
            bgcolor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 1,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            mt: 0.5,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 480,
          }}>
            {/* Шапка: поиск + кнопки */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid #E5E7EB', display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                autoFocus
                size="small"
                sx={{ flex: 1 }}
                placeholder="Поиск по имени или номеру..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); toggleAllFiltered(); }}
                sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}
              >
                {allFilteredSelected ? 'Снять отфильтр.' : `Выбрать все (${filtered.length})`}
              </Button>
              {selected.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); onChange([]); }}
                  sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontSize: '0.78rem' }}
                >
                  Снять все
                </Button>
              )}
            </Box>

            {/* Список — 2 колонки */}
            <Box sx={{ overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'start' }}>
              {filtered.length === 0 ? (
                <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center', gridColumn: '1 / -1' }}>
                  Ничего не найдено
                </Typography>
              ) : filtered.map(acc => (
                <MenuItem
                  key={acc.id}
                  dense
                  onClick={(e) => { e.stopPropagation(); toggle(acc.name); }}
                  selected={selected.includes(acc.name)}
                  sx={{ py: 0.75, px: 1.5 }}
                >
                  <Checkbox size="small" checked={selected.includes(acc.name)} sx={{ p: 0.5, mr: 1 }} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" fontWeight={selected.includes(acc.name) ? 600 : 400} noWrap>
                      {acc.name}
                    </Typography>
                    {acc.phone_number && (
                      <Typography variant="caption" color="text.secondary" noWrap>{acc.phone_number}</Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
              {totalFiltered > 50 && (
                <Typography sx={{ p: 1.5, fontSize: '0.75rem', color: '#6B7280', textAlign: 'center', gridColumn: '1 / -1' }}>
                  Показаны первые 50 — уточните поиск
                </Typography>
              )}
            </Box>

            {/* Футер */}
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">
                {search ? `Найдено: ${totalFiltered}` : `Всего: ${accounts.length}`}
              </Typography>
              <Typography variant="caption" fontWeight={600} color="#1E40AF">
                Выбрано: {selected.length}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

// === SetupTab, ReactionsField, EmojiPicker, ActiveTab ===

const SetupTab: React.FC<any> = ({ form, update, accounts, groups, start, isLoading, gridItem, addReaction, removeReaction, validation }) => (
  <Grid container spacing={2}>
    <Grid size={12} sx={gridItem}>
      <AccountPickerMulti
        accounts={accounts}
        selected={form.accountNames}
        onChange={(names: string[]) => update('accountNames', names)}
        groups={groups}
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <TextField
        label="Каналы"
        value={form.channels}
        onChange={(e) => update('channels', e.target.value)}
        fullWidth
        required
        placeholder="@channel1, @channel2, t.me/channel3"
        helperText="Введите один или несколько каналов через запятую"
      />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <ReactionsField reactions={form.reactions} addReaction={addReaction} removeReaction={removeReaction} />
    </Grid>

    <Grid size={6} sx={gridItem}>
      <TextField label="Мин. задержка (сек)" type="number" {...validation.fieldProps('minDelay', form, update)} fullWidth />
    </Grid>
    <Grid size={6} sx={gridItem}>
      <TextField label="Макс. задержка (сек)" type="number" {...validation.fieldProps('maxDelay', form, update)} fullWidth />
    </Grid>

    <Grid size={12} sx={gridItem}>
      <FormControlLabel
        control={<Checkbox checked={form.spreadReactions} onChange={(e) => update('spreadReactions', e.target.checked)} />}
        label="Растягивать реакции по времени"
      />
    </Grid>

    {form.spreadReactions && (
      <Grid size={12} sx={gridItem}>
        <TextField label="Макс. время растягивания (часы)" type="number" {...validation.fieldProps('maxSpreadHours', form, update)} fullWidth />
      </Grid>
    )}
    <Grid size={12} sx={gridItem}>
  <TextField
    label="Сколько аккаунтов ставят реакцию"
    type="number"
    {...validation.fieldProps('reactionsCount', form, update)}
    fullWidth
    placeholder="Оставьте пустым — будут реагировать все"
  />
</Grid>

<Grid size={12}>
  <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.6 }}>
    Если указать число — только это количество аккаунтов будет ставить реакции на каждый новый пост.
    Остальные аккаунты просто оставят просмотры.
    Выбор происходит случайно для каждого сообщения.
  </Typography>
</Grid>

    <Grid size={12} sx={gridItem}>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.randomizeReactionsCount}
            onChange={(e) => update('randomizeReactionsCount', e.target.checked)}
          />
        }
        label="Рандомизировать число реагирующих"
      />
    </Grid>

    {form.randomizeReactionsCount && (
      <Grid size={12}>
        <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.6 }}>
          На каждый пост случайно выбирается от 60% до 100% от указанного числа реагирующих.
          Смотрят при этом всегда все аккаунты.
        </Typography>
      </Grid>
    )}

    <Grid size={12} sx={gridItem}>
      <TextField
        label="Мин. число просматривающих"
        type="number"
        {...validation.fieldProps('minViewsCount', form, update)}
        fullWidth
        placeholder="Оставьте пустым — смотрят все"
      />
    </Grid>

    <Grid size={12}>
      <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.6 }}>
        Минимальное число аккаунтов, которые просматривают каждый пост.
        Фактическое число смотрящих выбирается случайно от этого значения до общего числа аккаунтов.
        Должно быть не меньше числа реагирующих.
      </Typography>
    </Grid>
    <Grid size={12} sx={gridItem}>
      <Button
        variant="contained"
        onClick={start}
        disabled={isLoading || !form.accountNames.length || !form.channels.trim() || form.reactions.length === 0 || validation.hasErrors}
        startIcon={<PlayArrowIcon />}
        sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' }, py: 2,  fontSize: '1.1rem', }}
        fullWidth
      >
        {isLoading ? <CircularProgress size={20} /> : 'Запустить'}
      </Button>
    </Grid>
  </Grid>
);

const ReactionsField: React.FC<any> = ({ reactions, addReaction, removeReaction }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          p: 2,
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          cursor: 'pointer',
          transition: 'all .2s',
          '&:hover': { borderColor: '#3B82F6' }
        }}
      >
        {reactions.length === 0 && (
          <Typography color="text.secondary">
            Выберите эмодзи
          </Typography>
        )}

        {reactions.map((emoji: string) => (
            <Box
                key={emoji}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: '#EFF6FF',
                  fontSize: '1.6rem',
                  transition: 'all .2s',
                  '&:hover': {bgcolor: '#DBEAFE'}
                }}
            >
              {emoji}

              <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    removeReaction(emoji);
                  }}
                  sx={{
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    opacity: 0.6,
                    '&:hover': {opacity: 1}
                  }}
              >
                ✕
              </Box>
            </Box>
        ))}

        <Box sx={{ ml: 'auto', fontSize: '0.75rem', color: '#6B7280' }}>
          {reactions.length}/10
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography mb={1} fontWeight={600}>
            Выберите реакцию
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 1
            }}
          >
            {POPULAR_EMOJI.map((emoji) => (
              <Box
                key={emoji}
                onClick={() => {
                  addReaction(emoji);
                  setAnchorEl(null);
                }}
                sx={{
                  fontSize: '1.8rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  py: 1,
                  transition: 'all .15s',
                  '&:hover': {
                    bgcolor: '#F3F4F6',
                    transform: 'scale(1.2)'
                  }
                }}
              >
                {emoji}
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

const EmojiPicker: React.FC<any> = ({ open, onClose, onSelect }) => (
  <Drawer anchor="bottom" open={open} onClose={onClose}>
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" textAlign="center" gutterBottom>Выберите реакцию</Typography>
      <Grid container spacing={2} justifyContent="center">
        {POPULAR_EMOJI.map(emoji => (
          <Grid key={emoji} size={2}>
            <MuiIconButton onClick={() => { onSelect(emoji); onClose(); }} sx={{ fontSize: '2.5rem' }}>
              {emoji}
            </MuiIconButton>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Drawer>
);


const ActiveTab: React.FC<{
  jobs: ActiveReactor[];
  stop: (id: string) => Promise<void>;
  isLoading: boolean;
  setMessage: (msg: { text: string; type: 'success' | 'error' | 'info' } | null) => void;
  fetchActive: () => Promise<void>;
  accounts: Account[]
}> = ({ jobs, stop, isLoading, setMessage, fetchActive, accounts }) => {
  const navigate = useNavigate();

  // ← ВСЁ ВНУТРИ КОМПОНЕНТА — КАК И ДОЛЖНО БЫТЬ!
  const [deleting, setDeleting] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  const handleDelete = async (jobId: string) => {
    if (!window.confirm('Удалить задачу НАВСЕГДА? Это нельзя отменить.')) return;

    setDeleting(jobId);
    try {
      await api.get(`/reactors/delete/${jobId}`);
      setMessage({ text: 'Задача удалена', type: 'info' });
      await fetchActive();
    } catch (err: any) {
      setMessage({ text: err.response?.data?.detail || 'Ошибка при удалении', type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = async (jobId: string) => {
    setRestoring(jobId);
    try {
      await api.get(`/reactors/restore/${jobId}`);
      setMessage({ text: 'Задача возобновлена!', type: 'success' });
      await fetchActive();
    } catch (err: any) {
    const text = getUserFriendlyError(err, accounts, 'накрутку реакций');
    setMessage({ text, type: 'error' });
  } finally {
      setRestoring(null);
    }
  };

  if (jobs.length === 0) {
    return (
      <Typography textAlign="center" py={6} color="text.secondary" variant="h6">
        Нет активных задач
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Task ID</strong></TableCell>
            <TableCell><strong>Канал</strong></TableCell>
            <TableCell><strong>Аккаунты</strong></TableCell>
            <TableCell><strong>Просмотры</strong></TableCell>
            <TableCell><strong>Реакций</strong></TableCell>
            <TableCell><strong>Ошибки</strong></TableCell>
            <TableCell><strong>Статус</strong></TableCell>
            <TableCell align="right"><strong>Действия</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow
              key={job.job_id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/reactor/detail/${job.job_id}`)}
            >
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {job.job_id.slice(0, 8)}
              </TableCell>
              <TableCell>{job.channels.join(', ')}</TableCell>
              <TableCell>{job.accounts_count}</TableCell>

              <TableCell>
                {job.total_views_added}
              </TableCell>

              <TableCell>{job.total_reactions_added}</TableCell>
              <TableCell sx={{ color: job.total_errors ? '#DC2626' : 'inherit', fontWeight: 'medium' }}>
                {job.total_errors}
              </TableCell>
              <TableCell>
                <Chip
                  label={
                    job.status === 'running' ? 'Работает' :
                    job.status === 'stopped' ? 'Остановлен' :
                    job.status === 'dead' ? 'Умер' :
                    job.status === 'error' ? 'Ошибка' : job.status
                  }
                  size="small"
                  color={
                    job.status === 'running' ? 'success' :
                    job.status === 'stopped' ? 'warning' :
                    job.status === 'dead' || job.status === 'error' ? 'error' : 'default'
                  }
                />
              </TableCell>

              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                  {/* ВОЗОБНОВИТЬ */}
                  {job.status === 'stopped' && (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleRestore(job.job_id)}
                      disabled={restoring === job.job_id}
                      title="Возобновить задачу"
                    >
                      {restoring === job.job_id ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  )}

                  {/* ОСТАНОВИТЬ */}
                  {job.status === 'running' && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => stop(job.job_id)}
                      disabled={isLoading}
                      title="Остановить задачу"
                    >
                      <StopIcon fontSize="small" />
                    </IconButton>
                  )}

                  {/* УДАЛИТЬ */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(job.job_id)}
                    disabled={deleting === job.job_id}
                    title="Удалить навсегда"
                  >
                    {deleting === job.job_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NeuroReactor;